import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { StyledContainer } from '@zillow/zrs-comet';
import { Flex, mediaMixin, spaceMixin, Spinner } from '@zillow/constellation';
import {
  applicationsEnabledSelector,
  isEnrollmentLoadingSelector,
  isLandlordRestrictedSelector,
} from '../../selectors';
import EnrollmentSwitch from './EnrollmentSwitch';
import EnrollSuccessToast from './EnrollSuccessToast';
import EnrollmentStatus from './EnrollmentStatus';
import EnrollUpsellBanner from './EnrollUpsellBanner';
import { markRentalTermsAccepted } from '../../actions/rentalTermsActionCreators';
import {
  getUpdatedEnrollmentStatus as getUpdatedEnrollmentStatusAction,
  setEnrollmentStatus as setEnrollmentStatusAction,
} from '../../actions/enrollmentActionCreators';
import useQueryVariables from '../../hooks/useQueryVariables';

const StyledEnrollment = styled(StyledContainer)`
  padding-bottom: ${spaceMixin('xs')};

  ${mediaMixin(
    'lg',
    css`
      padding-bottom: ${spaceMixin('lg')};
    `,
  )}
`;

export default function Enrollment() {
  const enrollmentOn = useSelector(applicationsEnabledSelector);
  const isLandlordRestricted = useSelector(isLandlordRestrictedSelector);
  const isEnrollmentLoading = useSelector(isEnrollmentLoadingSelector);
  const { propertyId } = useQueryVariables();
  const dispatch = useDispatch();

  const setEnrollmentStatus = useCallback(
    (apiParams) => {
      dispatch(markRentalTermsAccepted());
      dispatch(setEnrollmentStatusAction(apiParams));
    },
    [dispatch],
  );

  useEffect(() => {
    // Enrollment can be updated on edit listing page which does not update the redux store
    // We must therefore check the BE for updates every time we load this component
    dispatch(getUpdatedEnrollmentStatusAction({ propertyId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLandlordRestricted) {
    return null;
  }

  if (isEnrollmentLoading) {
    return (
      <StyledEnrollment borderBottom>
        <Flex display="flex" justifyContent="center" alignItems="center" paddingBottom="sm">
          <Spinner size="md" />
        </Flex>
      </StyledEnrollment>
    );
  }

  return (
    <StyledEnrollment borderBottom id="enrollment">
      <EnrollSuccessToast enrollmentOn={enrollmentOn} />
      <EnrollUpsellBanner enrollmentOn={enrollmentOn} />
      <Flex display="flex" justifyContent="space-between" alignItems="center" gap="sm" marginTop="md" marginBottom="xs">
        <EnrollmentStatus enrollmentOn={enrollmentOn} />
        <EnrollmentSwitch enrollmentOn={enrollmentOn} setEnrollmentStatus={setEnrollmentStatus} />
      </Flex>
    </StyledEnrollment>
  );
}

Enrollment.propTypes = {
  listing: PropTypes.shape({
    propertyId: PropTypes.string.isRequired,
    street: PropTypes.string,
    unit: PropTypes.string,
  }).isRequired,
};
